/* eslint-disable no-case-declarations */
/* eslint-disable @typescript-eslint/ban-types */
import {
  BaseValidationDefinition,
  DataRuleDefinition,
  EntityModel,
  FieldRule,
  FormModel,
  OnChangeRule,
  SetIfEmptyLogicDefinition,
  SetLogicDefinition,
  ValidationDefinition,
} from '@msdk/definitions'
import { selectorFamily } from 'recoil'

import { createFunction, getExpressionForValidation, processCondition, processField } from '../../utils'
import { $entityModel } from '../metadata'

export interface ValidationCache {
  cachedRules: Record<string, BaseValidationDefinition[]>
  cachedListRules: Record<string, BaseValidationDefinition[]>
  cachedOnOneRowOfListRules: Record<string, ValidationDefinition[]>
  cachedOnAllRowsOfList: Record<string, ValidationDefinition[]>
  cachedFunctions: Record<string, Function>
}

export interface DataRuleCache {
  cachedFunctions: Record<string, Function>
  cachedListRules: Record<string, DataRuleDefinition[]>
  cachedOnOneRowOfListRules: Record<string, DataRuleDefinition[]>
  cachedOnAllRowsOfList: Record<string, DataRuleDefinition[]>
  rules: DataRuleDefinition[]
}

export interface OnChangeCache {
  cachedFunctions: Record<string, Function>
  rules: OnChangeRule[]
}

/**
 * return the field name, if an alias is passed the field name
 * will be prefixed with the given alias
 */
function getFieldName(field?: string, alias?: string) {
  if (!field) return undefined

  if (alias) {
    return `${alias}.${field}`
  }

  return field
}

/**
 * process the condition to change all reference to fields and prefix them
 * with the given alias, for example an expression linke:
 * `entity.Str('MY_FIELD')` would become: `entity.Str('AL.MY_FIELD')`
 */
function aliasCondition(condition: string, alias: string, model: EntityModel) {
  const fields = model.fields.map((f) => f.name)
  const result = fields.reduce((res, subStr) => {
    const pattern = new RegExp(subStr, 'g')
    return res.replace(pattern, `${alias}.${subStr}`)
  }, condition)
  return result
}

/**
 * clone the validation and change all references to fields to have the given alias
 * as prefix
 */
function aliasValidation(v: BaseValidationDefinition, alias: string, model: EntityModel) {
  const cloned = JSON.parse(JSON.stringify(v)) as BaseValidationDefinition
  cloned.onAllRowsOfList = getFieldName(cloned.onAllRowsOfList, alias)
  cloned.onList = getFieldName(cloned.onList, alias)
  cloned.onOneRowOfList = getFieldName(cloned.onOneRowOfList, alias)
  if (!cloned.onList) cloned.field = getFieldName(cloned.field, alias) as string
  switch (cloned.validationType) {
    case 'Expression':
      const validation = cloned as ValidationDefinition
      validation.condition = aliasCondition(validation.condition, alias, model)
      break
  }
  return cloned
}

function cacheValidations(model: EntityModel, cache: ValidationCache, formModel?: FormModel, alias?: string) {
  const rules = (model?.defaultRules || []).concat(formModel?.defaultRules ?? [])
  rules
    .filter((r) => r.validationType !== 'Invisible' && r.validationType !== 'Readonly')
    .forEach((r) => {
      if (alias) r = aliasValidation(r, alias, model)

      if (r.onList) {
        const entry = cache.cachedListRules[r.onList] ?? []
        entry.push(r)
        cache.cachedListRules[r.onList] = entry
      } else if (r.onOneRowOfList) {
        const entry = cache.cachedOnOneRowOfListRules[r.onOneRowOfList] ?? []
        entry.push(r as ValidationDefinition)
        cache.cachedOnOneRowOfListRules[r.onOneRowOfList] = entry
      } else if (r.onAllRowsOfList) {
        const entry = cache.cachedOnAllRowsOfList[r.onAllRowsOfList] ?? []
        entry.push(r as ValidationDefinition)
        cache.cachedOnAllRowsOfList[r.onAllRowsOfList] = entry
      } else {
        const entry = cache.cachedRules[r.field] ?? []
        entry.push(r)
        cache.cachedRules[r.field] = entry
      }

      const expression = getExpressionForValidation(r, !!r.onList)
      // eslint-disable-next-line no-new-func
      const func = createFunction(expression)
      cache.cachedFunctions[expression] = func
    })

  //extract from field attributes additional validations
  //for example the max length of the string
  model?.fields.forEach((f) => {
    /*
     * add validations only for fields that are actually
     * present in the form layout
     */
    const formDef = formModel?.getFormFieldDefinition(f.name)
    if (formDef && formModel) {
      const val = processField(f)
      val.forEach((v) => {
        if (v.onList) {
          const entry = cache.cachedListRules[v.onList] ?? []
          entry.push(v)
          cache.cachedListRules[v.onList] = entry
        } else {
          const entry = cache.cachedRules[v.field] ?? []
          entry.push(v)
          cache.cachedRules[v.field] = entry
        }

        const expression = getExpressionForValidation(v)
        // eslint-disable-next-line no-new-func
        const func = createFunction(expression)
        cache.cachedFunctions[expression] = func
      })
    }
  })
}

export const $validationCache = selectorFamily({
  key: 'ValidationCache',
  get:
    (params: Map<string, string>) =>
    ({ get }) => {
      const entityName = params.get('entityName') as string
      const formName = params.get('formName') as string

      const model = get($entityModel(entityName)) as EntityModel
      const formModel = model?.getForm(formName) as FormModel

      const cachedRules: Record<string, BaseValidationDefinition[]> = {}
      const cachedListRules: Record<string, BaseValidationDefinition[]> = {}
      const cachedOnOneRowOfListRules: Record<string, ValidationDefinition[]> = {}
      const cachedOnAllRowsOfList: Record<string, ValidationDefinition[]> = {}
      const cachedFunctions: Record<string, Function> = {}
      cachedFunctions['true'] = new Function('context', 'return true')

      const cache = { cachedRules, cachedListRules, cachedFunctions, cachedOnOneRowOfListRules, cachedOnAllRowsOfList }
      cacheValidations(model, cache, formModel)

      if (formModel?.linkEntities) {
        formModel.linkEntities.forEach((lk) => {
          const lkModel = get($entityModel(lk.entity)) as EntityModel
          cacheValidations(lkModel, cache, undefined, lk.as)
        })
      }

      return cache
    },
})

/**
 * clone the rule and change all references to fields to have the given alias
 * as prefix
 */
function aliasRule(rule: DataRuleDefinition, alias: string, model: EntityModel) {
  const cloned = JSON.parse(JSON.stringify(rule)) as DataRuleDefinition
  cloned.onAllRowsOfList = getFieldName(cloned.onAllRowsOfList, alias)
  cloned.onList = getFieldName(cloned.onList, alias)
  cloned.onOneRowOfList = getFieldName(cloned.onOneRowOfList, alias)
  cloned.condition = aliasCondition(cloned.condition, alias, model)
  if (!cloned.onList) {
    cloned.fieldRules.forEach((fr) => {
      fr.field = getFieldName(fr.field, alias) as string
    })
  }
  return cloned
}

function cacheRules(model: EntityModel, cache: DataRuleCache, formModel?: FormModel, alias?: string) {
  const rules = (model?.conditionalRules || []).filter((r) => r.type === 'Rule') as DataRuleDefinition[]
  const formRules = (formModel?.conditionalRules || []).filter((r) => r.type === 'Rule') as DataRuleDefinition[]
  const combinedRules = rules.concat(formRules)

  const defRules = (model?.defaultRules || []).concat(formModel?.defaultRules ?? [])
  defRules
    .filter((r) => r.validationType === 'Invisible' || r.validationType === 'Readonly')
    .forEach((r) => {
      if (alias) r = aliasValidation(r, alias, model)
      const fieldRule: FieldRule = {
        field: r.field,
        ruleType: 'FieldRule',
        readOnly: r.validationType === 'Readonly' ? true : undefined,
        visible: r.validationType === 'Invisible' ? false : undefined,
      }

      // NOTE: This block has been temporarily commented out.
      // It appears to cause suspected regressions with clearOnInvisible.
      // We are keeping it commented for now to avoid potential issues
      // and to facilitate future analysis and debugging.

      // if (r.validationType === 'Invisible') {
      //   const casted = r as InvisibleValidationDefinition
      //   fieldRule.clearOnInvisible = casted.clearOnInvisible
      // }

      const rule: DataRuleDefinition = {
        condition: 'true',
        type: 'Rule',
        onList: r.onList,
        fieldRules: [fieldRule],
      }

      if (r.onList) {
        const cached = cache.cachedListRules[r.onList] ?? []
        cached.push(rule)
        cache.cachedListRules[r.onList] = cached
      } else {
        cache.rules.push(rule)
      }

      const expression = getExpressionForValidation(r)
      // eslint-disable-next-line no-new-func
      const func = createFunction(expression)
      cache.cachedFunctions[expression] = func
    })

  combinedRules.forEach((r) => {
    if (alias) r = aliasRule(r, alias, model)
    if (r.onList) {
      const cached = cache.cachedListRules[r.onList] ?? []
      cached.push(r)
      cache.cachedListRules[r.onList] = cached
    } else if (r.onOneRowOfList) {
      const entry = cache.cachedOnOneRowOfListRules[r.onOneRowOfList] ?? []
      entry.push(r as DataRuleDefinition)
      cache.cachedOnOneRowOfListRules[r.onOneRowOfList] = entry
    } else if (r.onAllRowsOfList) {
      const entry = cache.cachedOnAllRowsOfList[r.onAllRowsOfList] ?? []
      entry.push(r as DataRuleDefinition)
      cache.cachedOnAllRowsOfList[r.onAllRowsOfList] = entry
    } else {
      cache.rules.push(r)
    }

    const expression = processCondition(r.condition)
    // eslint-disable-next-line no-new-func
    const func = createFunction(expression)
    cache.cachedFunctions[expression] = func
  })
}

export const $dataRuleCache = selectorFamily({
  key: 'DataRuleCache',
  get:
    (params: Map<string, string>) =>
    ({ get }) => {
      const entityName = params.get('entityName') as string
      const formName = params.get('formName') as string

      const model = get($entityModel(entityName)) as EntityModel
      const formModel = model?.getForm(formName) as FormModel

      const cachedFunctions: Record<string, Function> = {}
      cachedFunctions['true'] = new Function('context', 'return true')

      const cachedListRules: Record<string, DataRuleDefinition[]> = {}
      const cachedOnOneRowOfListRules: Record<string, DataRuleDefinition[]> = {}
      const cachedOnAllRowsOfList: Record<string, DataRuleDefinition[]> = {}
      const rules: DataRuleDefinition[] = []
      const cache = {
        cachedFunctions,
        rules,
        cachedListRules,
        cachedOnOneRowOfListRules,
        cachedOnAllRowsOfList,
      }

      cacheRules(model, cache, formModel)

      if (formModel?.linkEntities) {
        formModel.linkEntities.forEach((lk) => {
          const lkModel = get($entityModel(lk.entity)) as EntityModel
          cacheRules(lkModel, cache, undefined, lk.as)
        })
      }

      return cache
    },
})

/**
 * clone the change rule and change all references to fields to have the given alias
 * as prefix
 */
function aliasChangeRule(rule: OnChangeRule, alias: string, model: EntityModel) {
  const cloned = JSON.parse(JSON.stringify(rule)) as OnChangeRule
  cloned.onList = getFieldName(cloned.onList, alias)
  cloned.condition = aliasCondition(cloned.condition, alias, model)
  if (!cloned.onList) {
    cloned.changeOnFieldList = cloned.changeOnFieldList.map((f) => getFieldName(f, alias) as string)
    cloned.actions.forEach((a) => {
      a.field = getFieldName(a.field, alias) as string
    })
  }
  return cloned
}

function cacheChangeRules(model: EntityModel, cache: OnChangeCache, formModel?: FormModel, alias?: string) {
  const rules = (model?.conditionalRules || []).filter((r) => r.type === 'Logic') as OnChangeRule[]
  const formRules = (formModel?.conditionalRules ?? []).filter((r) => r.type === 'Logic') as OnChangeRule[]
  const combinedRules = rules.concat(formRules)
  combinedRules.forEach((r) => {
    if (alias) r = aliasChangeRule(r, alias, model)
    const expression = processCondition(r.condition)
    // eslint-disable-next-line no-new-func
    const func = createFunction(expression)
    cache.cachedFunctions[expression] = func
    r.actions.forEach((a) => {
      switch (a.logicType) {
        case 'Set': {
          const setDef = a as SetLogicDefinition
          // eslint-disable-next-line @typescript-eslint/no-shadow
          const func = createFunction(setDef.value)
          cache.cachedFunctions[setDef.value] = func
          break
        }
        case 'SetIfEmpty': {
          const setDef = a as SetIfEmptyLogicDefinition
          // eslint-disable-next-line @typescript-eslint/no-shadow
          const func = createFunction(setDef.value)
          cache.cachedFunctions[setDef.value] = func
          break
        }
        default:
          break
      }
    })
    cache.rules.push(r)
  })
}

export const $onChangeCache = selectorFamily({
  key: 'OnChangeCache',
  get:
    (params: Map<string, string>) =>
    ({ get }) => {
      const entityName = params.get('entityName') as string
      const formName = params.get('formName') as string

      const model = get($entityModel(entityName)) as EntityModel
      const formModel = model?.getForm(formName) as FormModel

      const cachedFunctions: Record<string, Function> = {}
      const cache = { cachedFunctions, rules: [] }

      cacheChangeRules(model, cache, formModel)

      if (formModel?.linkEntities) {
        formModel.linkEntities.forEach((lk) => {
          const lkModel = get($entityModel(lk.entity)) as EntityModel
          cacheChangeRules(lkModel, cache, undefined, lk.as)
        })
      }

      return cache
    },
})
/* eslint-enable no-case-declarations */
/* eslint-enable @typescript-eslint/ban-types */
